// Wortlaute der Erklärung, die jede Ansicht über die Schaltfläche neben ihrer Überschrift anbietet.
// Siehe docs/view-help.md.
//
// Die Erklärung läuft wie ein kleiner Film ab: pro Schritt ein Punkt auf der echten Oberfläche und
// ein kurzer Satz daneben, dann geht es von selbst weiter. Daraus folgen die Regeln für jeden Text:
//
//   1. Ein Schritt, ein Gedanke, ein bis zwei kurze Sätze. Es gibt keine Überschrift; der Punkt sitzt
//      auf dem Gegenstand, der Text muss ihn also nicht benennen. Wer mehr zu sagen hat, macht daraus
//      mehrere Schritte.
//   2. Kurze Hauptsätze, kein Gedankenstrich, keine Doppelpunkt-Konstruktion. Die Lesezeit eines
//      Schritts richtet sich nach seiner Länge, ein langer Text hält also die ganze Erklärung auf.
//   3. Er muss auch ohne Daten stimmen. "Hier stehen die Tage dieser Woche" trägt auch ohne eine
//      einzige Zeile, "Ihre erfassten Tage stehen hier" nicht. Im Rahmen läuft zwar eine erfundene
//      Beispielwoche, aber nicht jeder Bereich hängt an Daten.
//
// Die Schrittschlüssel sind innerhalb einer Tour eindeutig, nicht global. Deshalb liegt jeder
// Schritt unter seiner Tour und nicht in einem flachen `help.step.*`.
import timetracking from "@/plugins/i18n/de/help/timetracking"
import projects from "@/plugins/i18n/de/help/projects"
import projectcosts from "@/plugins/i18n/de/help/projectcosts"
import hourlywageinvoice from "@/plugins/i18n/de/help/hourlywageinvoice"
import customers from "@/plugins/i18n/de/help/customers"
import employees from "@/plugins/i18n/de/help/employees"
import materials from "@/plugins/i18n/de/help/materials"
import machines from "@/plugins/i18n/de/help/machines"
import packets from "@/plugins/i18n/de/help/packets"
import personnelplanning from "@/plugins/i18n/de/help/personnelplanning"
import settings from "@/plugins/i18n/de/help/settings"
import files from "@/plugins/i18n/de/help/files"
import daysubmissions from "@/plugins/i18n/de/help/daysubmissions"
import suppliers from "@/plugins/i18n/de/help/suppliers"

// Jede Tour hat ihre eigene Datei unter help/, damit Touren unabhängig voneinander geschrieben werden.
export default {
  trigger: {
    label: "Erklärung",
    aria: "Erklärung zu dieser Seite starten",
  },
  action: {
    // Nur als aria-label des ✕. Siehe services/a11y.ts: aria-label benennt, title ist der Tooltip.
    close: "Erklärung schließen",
  },
  // Die Taste, die der Zeiger bei einem Klick gedrückt hält, als Tastenkappe neben dem Pfeil. Die
  // Beschriftung der deutschen Tastatur.
  key: {
    ctrl: "Strg",
    shift: "Umschalt",
  },
  hint: {
    // Hängt am Punkt, zusammen mit dem Text des Schritts: "… Weiter".
    next: "Weiter",
    // Unter dem Text des letzten Schritts einer Seite. Danach klickt der Zeiger einen Reiter an und
    // die Seite wechselt; das soll niemanden überraschen.
    continue_to: "Weiter zu {view}",
  },
  player: {
    play: "Abspielen",
    pause: "Anhalten",
    replay: "Von vorn abspielen",
    // Die Schaltfläche, die das Menü öffnet, und das Menü selbst.
    speed: "Geschwindigkeit {speed}",
    speed_menu: "Abspielgeschwindigkeit",
    segment: "Schritt {number} von {total}",
    // Eine zugeklappte Seite in der Zeitleiste. Ein Klick klappt sie auf und zeigt ihre Schritte.
    section: "{view}, {count} Schritt, aufklappen | {view}, {count} Schritte, aufklappen",
    // Die Vorschau über einer zugeklappten Seite.
    steps: "{count} Schritt | {count} Schritte",
    // Die Schaltfläche mit der Tastatur, die die Liste der Tastenkürzel öffnet.
    shortcuts: "Tastenkürzel",
  },
  // Die Liste der Tastenkürzel. Links stehen die Tasten, rechts steht, was sie tun. Kurz, weil jede
  // Zeile auf eine schmale Karte passen muss.
  shortcuts: {
    title: "Tastenkürzel",
    close: "Tastenkürzel schließen",
    play: "Abspielen oder anhalten",
    step: "Einen Schritt zurück oder weiter",
    view: "Eine Ansicht zurück oder weiter",
    jump: "Direkt zu einer Ansicht",
    edges: "Zum ersten oder letzten Schritt",
    speed: "Langsamer oder schneller",
    list: "Diese Liste ein- oder ausblenden",
    exit: "Erklärung schließen",
  },
  // Tastennamen, wie sie auf einer deutschen Tastatur stehen.
  keys: {
    space: "Leertaste",
    shift: "Umschalt",
    home: "Pos1",
    end: "Ende",
  },
  // Nur für Screenreader, wenn die Wiedergabe per Taste angehalten oder fortgesetzt wird.
  flash: {
    playing: "Wiedergabe läuft",
    paused: "Angehalten",
  },
  // Die Seite am Ende der Erklärung: was gesehen wurde, der Weg zurück in jede Seite, und hinaus.
  end: {
    title: "Erklärung „{tour}“ abgeschlossen",
    // "25 Schritte auf 6 Seiten"
    lead: "{steps} auf {pages}",
    pages: "{count} Seite | {count} Seiten",
    revisit: "Noch einmal ansehen",
    hint: "Sie starten sie jederzeit wieder über „{trigger}“ neben der Überschrift.",
    replay: "Von vorn",
    close: "Schließen",
  },
  aria: {
    dialog: "Erklärung zu dieser Seite",
    frame: "Die Anwendung mit Beispieldaten",
  },
  demo: {
    // Im Rahmen läuft die echte Anwendung mit einer erfundenen Beispielwoche, damit kein Schritt auf
    // eine leere Tabelle zeigt. Ein Wort genügt: die eigene Seite liegt unverändert hinter dem Dialog.
    chip: "Beispieldaten",
  },
  tour: {
    timetracking,
    projects,
    projectcosts,
    hourlywageinvoice,
    customers,
    employees,
    materials,
    machines,
    packets,
    personnelplanning,
    settings,
    files,
    daysubmissions,
    suppliers,
  },
}
